import { cn } from '@/lib/utils';
import { MoneyValueDto } from '@/types/MoneyValueDto.ts';
import { formatPrice } from '@/utils/formatters.ts';
import {
  formatMoneyExact,
  formatSignedPrice,
} from '@/features/dashboard/bookingChanges/utils/formatters.ts';
import { EMPTY_VALUE } from '@/components/ui/EmptyValue.tsx';

/**
 * Kwota modelu widoku — zawsze lista, po jednej pozycji na walutę.
 *
 * Rezerwacja bywa wielowalutowa (200 zł i 333 €) i żadna kwota tego nie zwija,
 * więc wszystkie miejsca, w których moduł pokazuje pieniądze, przechodzą przez
 * ten jeden komponent. Inaczej każde wymyśliłoby własny sposób pokazania drugiej
 * waluty — a pierwsze wersje tych ekranów po prostu ją gubiły.
 *
 * Waluty stoją jedna pod drugą, a nie w jednej linii: kwoty ze znakiem
 * („+ 200,00 zł”, „− 333,00 €”) sklejone w wiersz czyta się jak jedno działanie.
 * Kolumna wychodzi z `inline-block`, żeby dziedziczyć wyrównanie po rodzicu —
 * ta sama kwota stoi raz w prawej kolumnie tabeli, raz w zdaniu.
 */

export type MoneyAmountVariant =
  /** Kwota jak w księdze: „200,00 zł”, „− 40,00 zł”. */
  | 'plain'
  /** Delta paczki: „+ 200,00 zł”, „− 60,00 zł”. */
  | 'signed'
  /** Kwota bez znaku, gdy kierunek niesie sam napis („Dopłata”, „Zwrot”). */
  | 'absolute';

const formatters: Record<MoneyAmountVariant, (value: MoneyValueDto) => string> =
  {
    plain: formatMoneyExact,
    signed: formatSignedPrice,
    absolute: (value) => formatPrice(Math.abs(value.amount), value.currency),
  };

interface MoneyAmountListProps extends React.HTMLAttributes<HTMLSpanElement> {
  values: MoneyValueDto[];
  variant?: MoneyAmountVariant;
  /**
   * Co pokazać zamiast pustej listy. Pusta lista znaczy **brak kwoty**, a nie
   * zero — zera nie da się zmyślić, nie wiedząc, o którą walutę chodzi.
   */
  empty?: React.ReactNode;
}

const MoneyAmountList = ({
  values,
  variant = 'plain',
  empty = EMPTY_VALUE,
  className,
  ...props
}: MoneyAmountListProps) => {
  if (values.length === 0) {
    return (
      <span className={className} {...props}>
        {empty}
      </span>
    );
  }

  return (
    <span className={cn('inline-block', className)} {...props}>
      {values.map((value) => (
        <span key={value.currency} className="block whitespace-nowrap">
          {formatters[variant](value)}
        </span>
      ))}
    </span>
  );
};

export default MoneyAmountList;
